/**
 * The attachments on one message: image thumbnails and file chips.
 *
 * Every item opens the preview; a PDF chip and an image tile are the same control
 * with different faces. Removal is offered only where the caller says so -- the
 * uploader, on an open ticket.
 */
import { useState } from 'react'
import { FileText, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { isPreviewable, type TicketAttachment } from '@/api/support'
import { formatBytes } from '@/lib/format'
import { cn } from '@/lib/cn'

import { AttachmentPreview } from './AttachmentPreview'
import { useBlobUrl } from './useBlobUrl'

export function AttachmentList({
  attachments,
  filePath,
  onRemove,
  className,
}: {
  attachments: TicketAttachment[]
  filePath: (attachment: TicketAttachment, inline: boolean) => string
  onRemove?: (attachment: TicketAttachment) => void
  className?: string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState<number | null>(null)
  const previewable = attachments.filter(isPreviewable)

  if (attachments.length === 0) return null

  return (
    <>
      <ul
        className={cn('flex flex-wrap gap-2', className)}
        aria-label={t('support.attachments.title')}
      >
        {attachments.map((attachment) => {
          const previewIndex = previewable.indexOf(attachment)
          return (
            <li key={attachment.id} className="relative">
              <button
                type="button"
                className="group flex max-w-full items-center gap-2 rounded-md border border-border bg-surface p-1 pe-2 text-start text-xs hover:border-[var(--border-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                onClick={() => previewIndex >= 0 && setOpen(previewIndex)}
                aria-label={t('support.attachments.open', { name: attachment.filename })}
              >
                {attachment.media_type.startsWith('image/') ? (
                  <Thumbnail attachment={attachment} path={filePath(attachment, true)} />
                ) : (
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-surface-raised text-text-muted">
                    <FileText size={22} aria-hidden />
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block max-w-[10rem] truncate font-medium text-text">
                    {attachment.filename}
                  </span>
                  <span className="text-text-muted">{formatBytes(attachment.byte_size)}</span>
                </span>
              </button>
              {onRemove && (
                <button
                  type="button"
                  className="absolute -end-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-surface text-text-muted hover:text-text"
                  aria-label={t('support.attachments.remove', { name: attachment.filename })}
                  onClick={() => onRemove(attachment)}
                >
                  <X size={12} aria-hidden />
                </button>
              )}
            </li>
          )
        })}
      </ul>

      <AttachmentPreview
        attachments={previewable}
        index={open}
        onIndexChange={setOpen}
        onClose={() => setOpen(null)}
        filePath={filePath}
      />
    </>
  )
}

function Thumbnail({ attachment, path }: { attachment: TicketAttachment; path: string }) {
  const blob = useBlobUrl(path)
  return (
    <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded bg-surface-raised">
      {blob.status === 'ready' ? (
        <img src={blob.url} alt={attachment.filename} className="h-full w-full object-cover" />
      ) : (
        <FileText size={22} aria-hidden className="text-text-muted" />
      )}
    </span>
  )
}
